<template>
  <b-modal id="feelings_wrapper" title="Reflection" size="lg" @hidden="onModalHidden">
    <div>
      <!-- Date -->
      <div class="row mb-2">
        <div class="col-12">
          <div v-if="isReadonly" class="small text-muted mb-2">
            <i class="far fa-calendar-alt mr-1"></i>
            <span v-text="active_feeling.date_human"></span>
          </div>
          <template v-else>
            <b-form-datepicker
              v-model="active_feeling.date_edit"
              class="w-100"
              size="lg"
              :max="date_max"
              @input="validateForm"
            ></b-form-datepicker>
            <p v-if="!active_feeling.date_edit" class="p-2 text-danger">
              <i class="fas fa-times pr-1"></i>
              <span>Select a date above</span>
            </p>
          </template>
        </div>
      </div>

      <!-- Read-only view (viewing someone else's reflection) -->
      <div v-if="isReadonly && active_feeling.data">
        <div v-if="active_feeling.data.bars" class="mb-3">
          <div
            v-for="(val, key) in active_feeling.data.bars"
            :key="key"
          >
            <div v-if="val > 0" class="mb-2">
              <b-progress max="10" height="1.75rem">
                <b-progress-bar :value="val" :variant="setColour2(val)">
                  <div class="text-left pl-2">
                    <span v-text="key"></span>
                  </div>
                </b-progress-bar>
              </b-progress>
            </div>
          </div>
        </div>

        <div v-for="item in questions" :key="item.id">
          <div v-if="item.sanitised_value && item.id !== 'comments'" class="mb-3 pb-2 border-bottom">
            <p class="mb-1"><b v-text="item.title"></b></p>
            <div v-html="item.sanitised_value" class="pre-formatted text-muted"></div>
          </div>
        </div>
      </div>

      <!-- Editable view (own reflection) -->
      <div v-if="!isReadonly">
        <b-list-group>
          <b-list-group-item
            v-for="item in feelings"
            :key="item.id"
            :class="setColour(item.id)"
          >
            <label class="pre-formatted">
              <b v-text="item.title"></b>:
              <small v-text="item.description"></small>
            </label>
            <b-row align-v="center">
              <div class="col-10">
                <input
                  v-if="!item.type || item.type !== 'text'"
                  type="range"
                  v-model="item.value"
                  class="form-range w-100"
                  min="0"
                  :max="item.max"
                  step="1"
                />
              </div>
              <div class="col-2 text-center text-white">
                <h1
                  v-if="item.id != 'overwhelm' && item.id != 'fatigue'"
                  v-text="item.value"
                ></h1>
                <h3 v-else v-text="yesNoText(item.value)"></h3>
              </div>
            </b-row>
          </b-list-group-item>

          <!-- questions inputs -->
          <b-list-group-item v-for="item in questions" :key="item.id">
            <hr v-if="item.id == 'goal'" />

            <div v-if="modal_type == 'edit'">
              <div v-if="item.id !== 'comments'">
                <label :class="{ 'gold h5': item.id == 'goal' }" class="pre-formatted">
                  <span v-if="item.description === ''">
                    <b v-text="item.title"></b>:<br />
                    <small v-text="item.subtitle"></small>
                  </span>
                  <a v-else v-b-toggle :href="'#hint-' + item.id" @click.prevent class="text-secondary">
                    <i class="fas fa-question-circle pr-2 gold"></i>
                    <b v-text="item.title"></b>:<br />
                    <small class="pl-4" v-text="item.subtitle"></small>
                  </a>
                </label>
                <b-collapse v-if="item.description !== ''" :id="'hint-' + item.id">
                  <p v-text="item.description" class="small bronze pre-formatted"></p>
                </b-collapse>
                <b-form-textarea
                  v-if="!item.type || item.type !== 'text'"
                  v-model="item.sanitised_value"
                  :placeholder="item.placeholder"
                  rows="2"
                  max-rows="30"
                  class="mb-2"
                ></b-form-textarea>
              </div>
            </div>

            <div v-else>
              <div v-if="item.id !== 'challenge'">
                <label :class="{ 'gold h5': item.id == 'goal' }" class="pre-formatted">
                  <span v-if="item.description === ''">
                    <b v-text="item.title"></b>:<br />
                    <small v-text="item.subtitle"></small>
                  </span>
                  <a v-else v-b-toggle :href="'#hint-' + item.id" @click.prevent class="text-secondary">
                    <i class="fas fa-question-circle pr-2 gold"></i>
                    <b v-text="item.title"></b>:<br />
                    <small class="pl-4" v-text="item.subtitle"></small>
                  </a>
                </label>
                <b-collapse v-if="item.description !== ''" :id="'hint-' + item.id">
                  <p v-text="item.description" class="small bronze pre-formatted"></p>
                </b-collapse>
                <b-form-textarea
                  v-if="!item.type || item.type !== 'text'"
                  v-model="item.sanitised_value"
                  :placeholder="item.placeholder"
                  rows="2"
                  max-rows="30"
                  class="mb-2"
                  @change="saveToLocalStorage"
                ></b-form-textarea>
              </div>
            </div>
          </b-list-group-item>

          <b-list-group-item class="share-reflection-section">
            <div class="share-header mb-2">
              <i class="fas fa-share-alt mr-2"></i>
              <span>Share this Reflection</span>
            </div>
            <p class="share-hint">Tag connections — they'll get a notification and can leave comments.</p>
            <v-select
              v-model="shared_with"
              :options="connections"
              label="full_name"
              :reduce="c => c.id"
              multiple
              placeholder="Choose people to share with..."
              class="share-select"
            />
          </b-list-group-item>

          <b-list-group-item>
            <p v-if="!active_feeling.date_edit" class="p-0 m-0 text-danger small">
              <i class="fas fa-times pr-1"></i> Select a date above
            </p>
            <button
              type="button"
              class="btn btn-primary"
              @click="submit"
              :disabled="!can_submit"
            >
              <i class="fas fa-cloud-sun-rain fa-1x mr-1"></i>
              <span v-if="modal_type == 'add'">Log</span><span v-else>Edit</span> Reflection
            </button>
            <b-button
              v-if="modal_type === 'edit'"
              size="sm"
              variant="outline-danger"
              @click="removeFeeling"
            >
              <i class="fas fa-trash-alt"></i> Delete
            </b-button>
          </b-list-group-item>
        </b-list-group>
      </div>

      <div v-if="active_feeling.replies" class="col-12 pt-2 mt-2 mb-4">
        <h4 class="border-bottom pb-2 pt-2 gold">Comments</h4>

        <div
          v-for="R in active_feeling.replies"
          :key="R.id"
          :class="'R_' + R.id"
          class="mb-4 pb-2 border-bottom"
        >
          <div class="row">
            <div class="col-md-12">
              <p class="small text-right">
                <i class="fas fa-user pr-1"></i>
                <span v-text="R.user_name"></span><br />
                <i class="far fa-clock pr-1"></i>
                <span v-text="R.access.date"></span> -
                <span v-text="R.access.time" class=""></span>
              </p>
              <div>
                <span v-html="R.main_text" class="pre-formatted"></span>
              </div>
            </div>
          </div>

          <div
            v-if="
                (R.youtube && R.youtube.length > 0) ||
                  (R.vimeo && R.vimeo.length > 0)
              "
            class=""
          >
            <video-embeds :embeddata="R" classW="col-md-6 mb-2" />
          </div>
        </div>
      </div>

      <div v-if="modal_type !== 'add'" class="col-12 mb-2 mt-2">
        <p class="p-0 m-0 mt-3">
          <b>Comments / Notes</b>
          <small
            v-text="questions.comments.description"
          ></small>
        </p>
        <b-form-textarea
          v-model="reply_text"
          placeholder="Add any notes here"
          rows="6"
          max-rows="30"
          class="my-2"
          @change="saveToLocalStorage"
        ></b-form-textarea>
        <b-button size="sm" variant="primary" @click="replyAdd()">
          Add Comment
        </b-button>
      </div>
    </div>

    <template #modal-footer="{ cancel }">
      <div>
        <b-button size="sm" variant="outline-secondary" @click="cancel()">
          Close
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import axios from "@/axios";
import VideoEmbeds from "@/components/VideoEmbeds";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  components: {
    VideoEmbeds,
    vSelect,
  },
  props: {
    readonly: {
      type: Number,
      default: 1,
    },
    modal_type: {
      type: String
    },
    feeling: {
      type: Object
    },
    feelings: {
      type: Object
    },
    questions: {
      type: Object
    },
  },
  data() {
    return {

      active_feeling: {...this.feeling},
      can_submit: true,

      date_max: new Date(),
      search_text: "",
      searching: 1,

      reply_text: "",
      connections: [],
      shared_with: this.feeling.shared_with ?? [],

      max_progress: 30,
      message: {
        text: "Select a date above",
        class: "text-danger",
        icon: "fas fa-times",
      },
      history: [],

      feelings_total: 0,
      goal: "",
      goal_latest: "",
    };
  },
  computed: {
    isReadonly() {
      return this.readonly == 1 && this.modal_type === 'edit';
    },
  },
  watch: {
    feeling: {
      handler(newVal) {
        this.active_feeling = { ...newVal };
        this.shared_with = newVal.shared_with
      },
      deep: true
    },
  },
  mounted() {
    this.fetchConnections();
  },
  methods: {

    async fetchConnections() {
      try {
        const { data } = await axios.get('connections/connected/list');
        this.connections = data || [];
      } catch (e) {
        console.error('fetchConnections:', e);
      }
    },

    replaceURLs(text) {
      if (!text) return;

      var urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
      return text.replace(urlRegex, function(url) {
        var hyperlink = url;
        if (!hyperlink.match("^https?:\/\/")) {
          hyperlink = "http://" + hyperlink;
        }
        return (
          '<a href="' +
          hyperlink +
          '" style="color:#d6b674;" target="_blank" rel="noopener noreferrer">' +
          url +
          "</a>"
        );
      });
    },

    ObjectLength(object) {
      return Object.keys(object).length;
    },

    removeFeeling() {
      var c = confirm("Are you sure!");
      if (c === true) {

        const params = new URLSearchParams();
        params.append("token", this.$store.state.session_id);
        params.append("forum_id", this.active_feeling.id);

        axios({
          method: "post",
          url: this.$store.state.api_url + "feelingsRemove",
          data: params,
        })
          .then(() => {
            this.$emit('onAction')
          })
          .catch(function(error) {
            console.log(error);
          });
      }
    },

    replyAdd() {
      let _this = this;

      _this.reply_text = this.replaceURLs(_this.reply_text);
      let params = new URLSearchParams();
      params.append("main_text", _this.reply_text);
      params.append("post_id", _this.active_feeling.id);

      axios({
        method: "post",
        url: _this.$store.state.api_url + "feelingsReply",
        data: params,
      })
        .then(function(response) {
          if (response.data.status == 1) {
            const added = response.data.data?.added_data;
            if (added) {
              _this.active_feeling = {
                ..._this.active_feeling,
                replies: { ...(_this.active_feeling.replies || {}), ...added }
              };
            }
            _this.$emit('onComment');
            _this.reply_text = "";
          }
        })
        .catch(function(error) {
          console.log(error);
        });
    },

    setColour(val) {
      var C = "bronze_bgd";
      if (this.feelings[val].value > 7) {
        C = "gold_bgd";
      } else if (this.feelings[val].value > 4) {
        C = "cl_bg_blue"; //'silver_bgd';
      } else if (this.feelings[val].value <= 0) {
        C = "light_silver_bgd";
      }
      return C;
    },
    setColour2(val) {
      var C = "bronze_bgd";
      if (val > 7) {
        C = "gold_bgd";
      } else if (val > 4) {
        C = "cl_bg_blue"; //'silver_bgd';
      } else if (val <= 0) {
        C = "light_silver_bgd";
      }
      return C;
    },

    shareData() {
      this.$bvModal.show("share_wrapper");
    },

    submit() {


      this.validateForm()

      if (! this.can_submit) {
        return
      }


      let _this = this;

      _this.can_submit = false;

      _this.message.icon = "fas fa-spinner fa-spin";

      let params = new URLSearchParams();
      params.append("token", this.$store.state.session_id);
      params.append("date", this.active_feeling.date_edit);

      const feelings = Object.values(this.feelings);
      for (const key of feelings) {
        params.append("feelings[" + key.id + "]", key.value);
      }

      const questions = Object.values(this.questions);
      console.log(questions);

      for (const question of questions) {
        let value = question.sanitised_value;
        if (question["value"]) {
          try {
            let obj = JSON.parse(question["value"]);
            obj["text"] = question["sanitised_value"];
            value = JSON.stringify(obj);
          } catch (error) {
            console.error(error);
          }
        }
        params.append("questions[" + question.id + "]", value);
      }

      if (_this.modal_type == "edit") {
        params.append("forum_id", _this.active_feeling.id);
      }

      _this.shared_with.forEach(id => params.append("shared_with[]", id));

      axios({
        method: "post",
        url: _this.$store.state.api_url + "feelingsAdd",
        data: params,
      })
        .then(function(response) {
          if (response.data.status === 1) {
            _this.message.text = "";
            _this.message.icon = "fas fa-check";
            _this.$emit('onAction')
            localStorage.removeItem("feelingsLog");
          } else {
            _this.message.text = response.data.message;
            _this.message.icon = "fas fa-times";
          }
        })
        .catch(function(error) {
          console.log(error);
        });
    },

    validateForm() {
      // can only submit if date_selected is not empty.
      if (this.active_feeling.date_edit) {
        this.can_submit = true;
        this.message.text = "";
        this.message.class = "";
        this.message.icon = "";
      } else {
        this.can_submit = false;
        this.message.text = "Select a date above";
        this.message.class = "text-danger";
        this.message.icon = "fas fa-times";
      }
      this.saveToLocalStorage();
    },

    yesNoText(val) {
      if (val == 0) return "No";
      else return "Yes";
    },

    saveToLocalStorage() {
      // save value to localStorage only if in add mode and after mountng.
      if (this.modal_type === "add") {
        let log = {
          feelings: JSON.parse(JSON.stringify(this.feelings)),
          questions: JSON.parse(JSON.stringify(this.questions)),
          date_selected: this.active_feeling.date_edit,
        };
        localStorage.setItem("feelingsLog", JSON.stringify(log));
      }
    },

    onModalHidden() {
      if (this.$route.params.id) {
        this.$router.replace({ path: this.$route.path.replace(/\/\d+$/, ''), query: {} }).catch(() => {});
      }
    },

    cancel() {
      console.log("cancel");
      for (let key in this.questions) {
        this.questions[key]["sanitised_value"] = "";
      }
    },
  },
}
</script>

<style scoped>
  .pre-formatted {
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  .share-reflection-section {
    background: linear-gradient(135deg, #fdf6e3, #fef9ee);
    border-left: 4px solid #d6b674 !important;
  }

  .share-header {
    font-size: 1rem;
    font-weight: 700;
    color: #c4a55a;
    letter-spacing: 0.02em;
  }

  .share-hint {
    font-size: 0.8rem;
    color: #888;
    margin-bottom: 10px;
  }
</style>
